import { fmtMonthDayYear, utcToWall } from '@concord/domain';
import { Button, Dialog, Field, KeyValue, Notice, Panel, PanelHead, useToast } from '@concord/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';

interface Membership {
  planId: string;
  plan: string;
  customer: string;
  validFrom: string;
  validTo: string | null;
  current: boolean;
}
interface PlansView {
  history: Membership[];
  /** The patient's sent, unfinished cases on their current plan. */
  openCases: number;
}

/**
 * A patient's plan: the current one, every earlier one, and (with account management)
 * moving them to another plan or ending their coverage. Every change is logged with a reason.
 */
export function PlanMembership({
  accountId,
  name,
  tz,
  canManage,
}: {
  accountId: string;
  name: string;
  tz: string;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const key = ['account-plans', accountId];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api<PlansView>(`/staff/directory/account/${accountId}/plans`),
  });
  const [changing, setChanging] = useState(false);
  const [ending, setEnding] = useState(false);
  const day = (iso: string) => fmtMonthDayYear(new Date(iso), tz);
  const cur = q.data?.history.find((m) => m.current) ?? null;
  const ended = cur?.validTo ? new Date(cur.validTo) <= new Date() : false;
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: key });
    await qc.invalidateQueries({ queryKey: ['account', accountId] });
    await qc.invalidateQueries({ queryKey: ['plans-overview'] });
  };

  return (
    <Panel aria-label="Plan membership">
      <PanelHead title="Plan membership">
        {canManage && q.data && (
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: '.5rem' }}>
            <Button variant="sec" onClick={() => setChanging(true)}>
              Change plan
            </Button>
            {cur && !ended && (
              <Button variant="ghost" onClick={() => setEnding(true)}>
                End coverage
              </Button>
            )}
          </span>
        )}
      </PanelHead>
      <div className="pbody">
        {q.isError && <Notice tone="bad">{errorMessage(q.error)}</Notice>}
        {q.data && !cur && <span className="sub2">No plan.</span>}
        {cur && (
          <KeyValue
            rows={[
              ['Plan', `${cur.customer} › ${cur.plan}`],
              ['On it since', day(cur.validFrom)],
              ['Coverage ends', cur.validTo ? `${day(cur.validTo)}${ended ? ' (ended)' : ''}` : 'Open-ended'],
            ]}
          />
        )}
        {q.data && q.data.history.length > 1 && (
          <div style={{ marginTop: '.9rem' }}>
            <div className="sub2" style={{ fontWeight: 600, marginBottom: '.35rem' }}>
              Earlier plans
            </div>
            <KeyValue
              rows={q.data.history
                .filter((m) => !m.current)
                .map((m) => [
                  `${m.customer} › ${m.plan}`,
                  `${day(m.validFrom)} to ${m.validTo ? day(m.validTo) : 'open-ended'}`,
                ])}
            />
          </div>
        )}
      </div>
      {q.data && (
        <ChangePlan
          open={changing}
          onOpenChange={setChanging}
          accountId={accountId}
          name={name}
          currentPlanId={cur && !ended ? cur.planId : null}
          openCases={q.data.openCases}
          onDone={async (r) => {
            await refresh();
            setChanging(false);
            toast(
              r.moved.length
                ? `Plan changed. ${r.moved.join(', ')} moved to the new plan.${r.kept.length ? ` ${r.kept.join(', ')} kept the old plan (case type not covered).` : ''}`
                : 'Plan changed.',
            );
          }}
        />
      )}
      {cur && (
        <EndCoverage
          open={ending}
          onOpenChange={setEnding}
          accountId={accountId}
          name={name}
          plan={cur.plan}
          tz={tz}
          onDone={async () => {
            await refresh();
            setEnding(false);
            toast('Coverage end date saved.');
          }}
        />
      )}
    </Panel>
  );
}

function ChangePlan({
  open,
  onOpenChange,
  accountId,
  name,
  currentPlanId,
  openCases,
  onDone,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  accountId: string;
  name: string;
  currentPlanId: string | null;
  openCases: number;
  onDone: (r: { moved: string[]; kept: string[] }) => Promise<void>;
}) {
  const plans = useQuery({
    queryKey: ['plans'],
    queryFn: () => api<{ items: { id: string; name: string; customer: string }[] }>('/staff/directory/plans'),
    enabled: open,
  });
  const [planId, setPlanId] = useState('');
  const [reason, setReason] = useState('');
  const [moveCases, setMoveCases] = useState(true);
  const save = useMutation({
    mutationFn: () =>
      api<{ moved: string[]; kept: string[] }>(`/staff/directory/account/${accountId}/plan`, {
        body: { planId, reason: reason.trim(), moveCases },
      }),
    onSuccess: async (r) => {
      setPlanId('');
      setReason('');
      await onDone(r);
    },
  });
  const options = (plans.data?.items ?? []).filter((p) => p.id !== currentPlanId);
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Change ${name}'s plan`}
      description="From now on, new cases use the new plan. The change and your reason are logged."
      footer={
        <>
          <Button variant="sec" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            busy={save.isPending}
            disabled={!planId || reason.trim().length < 3}
            onClick={() => save.mutate()}
          >
            Change plan
          </Button>
        </>
      }
    >
      <Field label="New plan">
        {(id) => (
          <select id={id} className="select" value={planId} onChange={(e) => setPlanId(e.target.value)}>
            <option value="">Choose a plan</option>
            {options.map((p) => (
              <option key={p.id} value={p.id}>
                {p.customer} › {p.name}
              </option>
            ))}
          </select>
        )}
      </Field>
      <Field label="Reason" hint="For example: changed employer, enrolled in the wrong plan.">
        {(id, d) => (
          <input
            id={id}
            className="input"
            aria-describedby={d}
            value={reason}
            maxLength={300}
            onChange={(e) => setReason(e.target.value)}
          />
        )}
      </Field>
      {openCases > 0 && (
        <label style={{ display: 'flex', gap: '.55rem', alignItems: 'flex-start', fontSize: '.88rem' }}>
          <input type="checkbox" checked={moveCases} onChange={(e) => setMoveCases(e.target.checked)} />
          <span>
            Also move {openCases === 1 ? 'their open case' : `their ${openCases} open cases`} to the new plan.
            A case stays on the old plan if the new one does not cover its case type. Left on the old plan, an
            unreleased case shows the ended coverage and cannot be released.
          </span>
        </label>
      )}
      {save.isError && <Notice tone="bad">{errorMessage(save.error)}</Notice>}
    </Dialog>
  );
}

function EndCoverage({
  open,
  onOpenChange,
  accountId,
  name,
  plan,
  tz,
  onDone,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  accountId: string;
  name: string;
  plan: string;
  tz: string;
  onDone: () => Promise<void>;
}) {
  const today = utcToWall(new Date(), tz).date;
  const [endsOn, setEndsOn] = useState(today);
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () =>
      api(`/staff/directory/account/${accountId}/plan/end`, { body: { endsOn, reason: reason.trim() } }),
    onSuccess: async () => {
      setReason('');
      await onDone();
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`End ${name}'s coverage`}
      description={`${plan} covers them through the end of the day you choose, in their time zone. After that they cannot start a case, and cases not yet released cannot be released.`}
      footer={
        <>
          <Button variant="sec" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            busy={save.isPending}
            disabled={!endsOn || reason.trim().length < 3}
            onClick={() => save.mutate()}
          >
            End coverage
          </Button>
        </>
      }
    >
      <Field label="Covered through">
        {(id) => (
          <input
            id={id}
            type="date"
            className="input"
            min={today}
            value={endsOn}
            onChange={(e) => setEndsOn(e.target.value)}
          />
        )}
      </Field>
      <Field label="Reason">
        {(id) => (
          <input
            id={id}
            className="input"
            value={reason}
            maxLength={300}
            onChange={(e) => setReason(e.target.value)}
          />
        )}
      </Field>
      {save.isError && <Notice tone="bad">{errorMessage(save.error)}</Notice>}
    </Dialog>
  );
}
